import {
    FaroDeleteModal,
    FaroDialog,
    FaroIconButton,
    FaroMenu,
    FaroMenuProps,
    FaroOverlay,
    FaroOverlayTrigger,
    FaroTooltip,
} from '@faro/design-system';
import {
    ContentCopyIcon,
    DocumentNewIcon,
    DownloadIcon,
    MoreHorizontalIcon,
    RenameIcon,
    TabMoveIcon,
    Trash2Icon,
} from '@faro/icons';
import { StudyStatusType } from '@faro/study-designer-model';
import React, { ReactElement, useState } from 'react';
import { useAppPermissions } from '../../../../study-designer/package/auth';
import { useStudySpacePermissions } from '../../../package/react-hooks/useStudySpacePermissions';
import { StudyDefinitionTableRow } from '../StudyDefinitionTable.types';
import StudyDefinitionMoveModal from '../StudyDefintionMoveModal/StudyDefinitionMoveModal';
import styles from './StudyDefinitionMenu.module.scss';

export interface StudyDefinitionMenuProps extends FaroMenuProps, StudyDefinitionMenuCallbacks {
    studyDefinition: StudyDefinitionTableRow;
    index: number;
    isSubstudy?: boolean;
}

export type StudyDefinitionMenuCallbacks = Pick<
    StudyDefinitionTableRowCallbacks,
    'onRename' | 'onExport' | 'onClone' | 'onMove' | 'onDelete' | 'cloneDisabled' | 'onCreateSubstudy'
>;

export interface StudyDefinitionTableRowCallbacks {
    onRowClick: (row: StudyDefinitionTableRow) => void;
    onStatusTypeUpdate: (row: StudyDefinitionTableRow, type: StudyStatusType) => Promise<any> | void;
    onExport: (row: StudyDefinitionTableRow) => Promise<any> | void;
    onClone: (row: StudyDefinitionTableRow) => Promise<any> | void;
    onRename?: (row: StudyDefinitionTableRow) => Promise<any> | void;
    onMove: (row: StudyDefinitionTableRow, toSpaceId: string) => Promise<any> | void;
    onDelete: (row: StudyDefinitionTableRow) => Promise<any> | void;
    onCreateSubstudy: (
        parentRowId: string,
        studyToDuplicate: StudyDefinitionTableRow,
        useDefaultStudyId?: boolean
    ) => Promise<void>;
    cloneDisabled?: boolean;
}

function StudyDefinitionMenu(props: StudyDefinitionMenuProps): ReactElement {
    const {
        studyDefinition,
        index,
        onExport,
        onRename,
        onClone,
        onMove,
        onDelete,
        onCreateSubstudy,
        cloneDisabled = false,
        isSubstudy = false,
        ref,
    } = props;
    const [moveModalOpen, setMoveModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const permissions = useStudySpacePermissions();
    const appPermissions = useAppPermissions();

    const isMasterStudy = studyDefinition.parentId == null;
    const isMasterStudyLockedOrReadonly =
        (isMasterStudy && studyDefinition.status.type !== 'editable') ||
        (studyDefinition.parentStatus != null && studyDefinition.parentStatus.type !== 'editable');
    const hasChildren = studyDefinition.childRows != null && studyDefinition.childRows.length > 0;
    const anyChildrenLockedOrReadOnly = studyDefinition.childRows?.some(
        child => child.status.type === 'locked' || child.status.type === 'read-only'
    );

    const disableDelete = (hasChildren && anyChildrenLockedOrReadOnly) || isMasterStudyLockedOrReadonly;
    const disableMove = (hasChildren && anyChildrenLockedOrReadOnly) || isMasterStudyLockedOrReadonly;
    const disableDeleteToolTip = hasChildren
        ? `You cannot delete this study definition while it has read-only or locked substudies. Unlock or remove the substudies to proceed.`
        : `You cannot delete this substudy definition.  The master study definition must be in editable status to proceed.`;
    const disableMoveToolTip = isMasterStudyLockedOrReadonly
        ? `You cannot move this study while it is in read-only or locked status.  Change the study to editable to proceed.`
        : `You cannot move this study while it has read-only or locked substudies. Change the substudies status to editable or remove the substudies to proceed.`;
    // Aggregate menu items into a list
    const menuItems: React.JSX.Element[] = [];

    // Rename item
    if (permissions.editor && onRename != null && studyDefinition.status.type === 'editable') {
        menuItems.push(
            <FaroMenu.Item
                key="rename"
                left={<RenameIcon />}
                onClick={async () => {
                    await onRename(studyDefinition);
                }}
                data-testid="renameStudyDefinition"
            >
                Rename
            </FaroMenu.Item>
        );
    }

    // Move item
    if (permissions.editor && isMasterStudy) {
        menuItems.push(
            <FaroOverlayTrigger
                overlay={disableMove ? <FaroTooltip>{disableMoveToolTip}</FaroTooltip> : null}
                placement="top"
            >
                <FaroMenu.Item
                    key="move"
                    left={<TabMoveIcon />}
                    onClick={async () => {
                        await setMoveModalOpen(true);
                    }}
                    data-testid="moveStudyDefinition"
                    data-automation-id="moveStudyDefinition"
                    data-intercom-target="Move Study Definition"
                    disabled={disableMove}
                >
                    Move
                </FaroMenu.Item>
            </FaroOverlayTrigger>
        );
    }

    // Duplicate item
    if (permissions.editor) {
        menuItems.push(
            <FaroOverlayTrigger
                overlay={
                    !isMasterStudy && isMasterStudyLockedOrReadonly ? (
                        <FaroTooltip>
                            You cannot duplicate this study while the master study is in a read-only or locked status.
                            Change the master study status to editable to proceed.
                        </FaroTooltip>
                    ) : null
                }
                placement="top"
            >
                <FaroMenu.Item
                    key="duplicate"
                    left={<ContentCopyIcon />}
                    disabled={cloneDisabled || (!isMasterStudy && isMasterStudyLockedOrReadonly)}
                    onClick={async () => {
                        if (isSubstudy) {
                            await onCreateSubstudy(studyDefinition.parentId!, studyDefinition); // duplicating a substudy into a copy substudy then links it to master
                        } else {
                            await onClone(studyDefinition);
                        }
                    }}
                    data-testid="duplicateStudyDefinition"
                    data-automation-id="duplicateStudyDefinition"
                    data-intercom-target="Duplicate Study Definition"
                >
                    Duplicate
                </FaroMenu.Item>
            </FaroOverlayTrigger>
        );
    }

    // Duplicate as substudy item
    if (permissions.editor && onCreateSubstudy != null && isMasterStudy) {
        menuItems.push(
            <FaroOverlayTrigger
                overlay={
                    isMasterStudyLockedOrReadonly ? (
                        <FaroTooltip>
                            You cannot duplicate as a substudy while the master study is in a read-only or locked
                            status. Change the master study status to editable to proceed.
                        </FaroTooltip>
                    ) : null
                }
                placement="top"
            >
                <FaroMenu.Item
                    key="duplicateAsSubstudy"
                    left={<ContentCopyIcon />}
                    onClick={async () => {
                        await onCreateSubstudy(studyDefinition.id, studyDefinition); // Clone from master to substudy
                    }}
                    data-testid="duplicateAsSubstudy"
                    data-automation-id="duplicateAsSubstudy"
                    data-intercom-target="Duplicate as substudy"
                    disabled={isMasterStudyLockedOrReadonly}
                >
                    Duplicate as substudy
                </FaroMenu.Item>
            </FaroOverlayTrigger>
        );
    }

    // Create blank substudy item
    if (permissions.editor && onCreateSubstudy != null && isMasterStudy) {
        menuItems.push(
            <FaroOverlayTrigger
                overlay={
                    isMasterStudyLockedOrReadonly ? (
                        <FaroTooltip>
                            You cannot create a substudy while the master study is in a read-only or locked status.
                            Change the master study status to editable to proceed.
                        </FaroTooltip>
                    ) : null
                }
                placement="top"
            >
                <FaroMenu.Item
                    key="createBlankSubstudy"
                    left={<DocumentNewIcon />}
                    onClick={async () => {
                        await onCreateSubstudy(studyDefinition.id, studyDefinition, true); // Create from default template
                    }}
                    data-testid="createBlankSubstudy"
                    data-automation-id="createBlankSubstudy"
                    data-intercom-target="Create blank substudy"
                    disabled={isMasterStudyLockedOrReadonly}
                >
                    Create blank substudy
                </FaroMenu.Item>
            </FaroOverlayTrigger>
        );
    }

    // Export item
    if (appPermissions.studyDefinitionExport.write) {
        menuItems.push(
            <FaroMenu.Item
                key="export"
                left={<DownloadIcon />}
                onClick={async () => {
                    await onExport(studyDefinition);
                }}
                data-testid="exportStudyDefinition"
                data-automation-id="exportStudyDefinition"
                data-intercom-target="Export Study Definition"
            >
                Export
            </FaroMenu.Item>
        );
    }

    // Delete item
    if (permissions.editor && studyDefinition.status.type === 'editable') {
        menuItems.push(
            <FaroOverlayTrigger
                key="delete"
                overlay={disableDelete ? <FaroTooltip>{disableDeleteToolTip}</FaroTooltip> : null}
                placement="top"
            >
                <FaroMenu.Item
                    left={<Trash2Icon />}
                    color="danger"
                    onClick={async () => {
                        await setDeleteModalOpen(true);
                    }}
                    data-testid="deleteStudyDefinition"
                    data-automation-id="deleteStudyDefinition"
                    data-intercom-target="Delete Study Definition"
                    disabled={disableDelete}
                >
                    Delete
                </FaroMenu.Item>
            </FaroOverlayTrigger>
        );
    }

    // Don't render the menu if there are no items
    if (menuItems.length === 0) {
        return <></>;
    }

    return (
        <>
            <FaroOverlayTrigger
                strategy="fixed"
                placement="bottom-end"
                openOnClick
                overlay={
                    <FaroMenu className={styles.menu} ref={ref} data-testid={`study-definition-menu-${index}`}>
                        {menuItems.map((item, index) => (
                            <React.Fragment key={index}>{item}</React.Fragment>
                        ))}
                    </FaroMenu>
                }
            >
                {(props: any, { open }: { open: boolean }) => (
                    <FaroIconButton
                        {...props}
                        icon={<MoreHorizontalIcon />}
                        appearance="inline"
                        data-open={open}
                        active={open}
                        className={`${styles.menuButton} ${open ? styles.open : ''}`}
                        data-testid={`studyDefinitionMenu-${index}`}
                    />
                )}
            </FaroOverlayTrigger>

            <FaroOverlay
                open={moveModalOpen}
                onClose={() => {
                    setMoveModalOpen(false);
                }}
            >
                <StudyDefinitionMoveModal
                    fromSpaceId={studyDefinition.spaceId}
                    onMove={(toSpaceId: string) => onMove(studyDefinition, toSpaceId)}
                    hasChildren={hasChildren}
                />
            </FaroOverlay>

            <FaroOverlay
                open={deleteModalOpen}
                onClose={() => {
                    setDeleteModalOpen(false);
                }}
            >
                <FaroDeleteModal onDelete={() => onDelete(studyDefinition)}>
                    <FaroDialog.Header>Delete</FaroDialog.Header>
                    <FaroDialog.Body>
                        This action will delete this study definition{hasChildren ? ' and all substudies' : ''}. It
                        cannot be undone.
                    </FaroDialog.Body>
                </FaroDeleteModal>
            </FaroOverlay>
        </>
    );
}

export default StudyDefinitionMenu;
